import React, { useEffect, useRef } from 'react';
import { ErrorSummaryProps } from '../../../types/DesignSystem/ErrorSummary';
import Alert from '../Alert/Alert';
import Link from '../Link/Link';
import { FOCUS_RING } from '../focusRing';
import { withClassName } from '../cn';
import { useMergedRef } from '../useMergedRef';
import { tabbablesIn } from '../tabbable';

export { ErrorSummaryScope } from '../errorSummaryScope';

/*
 * The control a line moves to. A field's `id` lands on its input; an id that names a GROUP (a
 * RadioGroup, a fieldset) is entered at its first stop, as Tab would enter it.
 */
const controlFor = (id: string): HTMLElement | null => {
  const target = document.getElementById(id);
  if (!target) return null;
  if (target.matches('input, select, textarea, button, a[href], [tabindex]')) return target;
  return tabbablesIn(target)[0] ?? target;
};

/**
 * ErrorSummary (§ 365) — see the type file for when to reach for it.
 *
 * An `Alert` in the danger tone, so the banner grammar (§ 63) is the one the page already has,
 * with three jobs of its own:
 *
 * - **It is announced once, and takes focus.** Live (`role="alert"`), and focused when errors
 *   first appear (and again on each failed submit, through `submitCount`), so a keyboard user
 *   lands where the next step starts. It never takes focus while the user is typing: an
 *   error list that CHANGES without a submit leaves focus where it is.
 * - **Each line links to its field.** The link moves focus to the control and scrolls it into
 *   view. `preventDefault` keeps the `#id` out of the address bar and away from the router.
 * - **Fields inside an `ErrorSummaryScope` go quiet** (see `errorSummaryScope.tsx`).
 */
const ErrorSummary = React.forwardRef<HTMLDivElement, ErrorSummaryProps>(function ErrorSummary({
  errors,
  title = 'There is a problem',
  submitCount,
  className = '',
  'data-testid': dataTestId,
  ...rest
}, forwardedRef) {
  const ownRef = useRef<HTMLDivElement>(null);
  const setRef = useMergedRef<HTMLDivElement>(ownRef, forwardedRef);
  const hasErrors = errors.length > 0;

  useEffect(() => {
    if (hasErrors) ownRef.current?.focus();
  }, [hasErrors, submitCount]);

  if (!hasErrors) return null;

  const moveTo = (id: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const control = controlFor(id);
    if (!control) return;
    control.focus();
    control.scrollIntoView?.({ block: 'center' });
  };

  return (
    <Alert
      {...rest}
      ref={setRef}
      tone="danger"
      title={title}
      live
      /* Focusable by script only: it is where a failed submit sends focus, not a Tab stop. */
      tabIndex={-1}
      className={withClassName(FOCUS_RING, className)}
      data-testid={dataTestId}
    >
      <ul className="tw-list-none tw-p-0 tw-m-0 tw-space-y-1">
        {errors.map((error) => (
          <li key={error.id}>
            <Link
              href={`#${error.id}`}
              onClick={moveTo(error.id)}
              data-testid={dataTestId ? `${dataTestId}-link-${error.id}` : undefined}
            >
              {error.message}
            </Link>
          </li>
        ))}
      </ul>
    </Alert>
  );
});

export default ErrorSummary;
